import { useEffect } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useFieldArray, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import DateInput from 'components/FormInputs/DateInput';
import AddressFieldArray from 'components/FormInputs/FieldArrays/AddressFieldArray';
import PhoneFieldArray from 'components/FormInputs/FieldArrays/PhoneFieldArray';
import SelectInput from 'components/FormInputs/SelectInput';
import TextInput from 'components/FormInputs/TextInput';

import { HighSchools } from '../utils/formConstants';
import { RegistrationFormOutputValues } from '../utils/validationSchema';

export function StudentInformationStep() {
  const { fields: parents, append: appendParent } = useFieldArray<RegistrationFormOutputValues>({
    name: 'user_parents',
  });
  // Watch date_of_birth
  const dateOfBirth = useWatch<RegistrationFormOutputValues, 'date_of_birth'>({ name: 'date_of_birth' });

  // add a parent when the student is under 18
  useEffect(() => {
    if (!dateOfBirth) return;

    const birthDate = dayjs(dateOfBirth);
    const studentAge = dayjs().diff(birthDate, 'year');

    // If under 18 and no parent fields exist, add a new parent
    if (studentAge < 18 && parents.length === 0) {
      appendParent({
        id: uuidv4(),
        first_name: '',
        last_name: '',
        relation: '',
        email: '',
        cell_phone: '',
        home_phone: '',
        work_phone: '',
        work_extension: '',
      });
    }
  }, [dateOfBirth, appendParent, parents.length]);

  return (
    <Stack gap={4}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography sx={{ fontWeight: 'bold', textDecoration: 'underline' }} variant="h5">
            Student Information
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput name="first_name" label="First Name" />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput name="middle_name" label="Middle Name" />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput name="last_name" label="Last Name" />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput name="nickname" label="Nickname" />
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <TextInput name="email" label="Student Email" />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <SelectInput
            name="sex"
            label="Sex"
            options={[
              { label: 'Male', value: 'male' },
              { label: 'Female', value: 'female' },
            ]}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <DateInput name="date_of_birth" disableFuture label="Date of Birth" />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SelectInput name="high_school" label="High School" options={HighSchools} fullWidth />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography sx={{ textDecoration: 'underline' }} variant="h5">
            <strong>Permit Information </strong>(If You Already Have One)
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextInput
            name="permit_num"
            label="Permit Number"
            helperText="Enter exactly as displayed including dashes."
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <DateInput name="permit_issued" disableFuture label="Permit Issue Date" />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <DateInput name="permit_expiration" disablePast label="Permit Expiration Date" />
        </Grid>
      </Grid>

      <PhoneFieldArray name="user_phones" />

      <AddressFieldArray name="user_addresses" />
    </Stack>
  );
}
